import { Box, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { DownloadAllButton } from "components/DownloadAllButton";
import { GeneratePdfButton } from "components/GeneratePdfButton";
import { TierIiReportEncampStatus } from "generated-graphql/graphql";
import { Outlet, useMatch } from "react-router-dom";
import { reportKindToLabel } from "util/constants";

import { StepperPageHeader } from "../StepperPageHeader";
import { useReport } from "../useReport";
import { ActivityStepper } from "./ActivityStepper";

export const Submitted: React.FC = () => {
  const theme = useTheme();
  const { data: report } = useReport();

  const isReportRoot = useMatch({
    path: "/o/:tenantId/chemicals/reports/:reportId",
    end: true,
  });

  const { reportingYear, reportKind, encampStatus } = report?.tierIIReport ?? {};
  const isFilingComplete = encampStatus === TierIiReportEncampStatus.FilingComplete;

  const reportKindPart = reportKind ? reportKindToLabel(reportKind).toLowerCase() : "";
  const statusPart = isFilingComplete
    ? "is filing complete"
    : "has been submitted to Encamp and is being verified and processed";
  const statusDescription = `Your ${reportingYear} ${reportKindPart} report ${statusPart}`;

  if (isReportRoot) {
    return (
      <Stack>
        <StepperPageHeader
          header="Congratulations! Report Submitted 💫"
          description={statusDescription}
          smallDescription={true}
        />
        {report && (
          <Stack gap={3}>
            <Stack gap={1}>
              <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={1}>
                {/* Left text */}
                <Stack direction="column" gap={0.5}>
                  <Box>
                    <Typography variant="subtitle1">Filing Progress</Typography>
                  </Box>
                  {!isFilingComplete && (
                    <Box>
                      <Tooltip title="During this time, Encamp will verify the data in your report, submit the report to applicable portals, and make any required fee payments (please note that some agencies invoice fee payments after a report has been filed). If you have any questions, please reach out to Encamp Support!">
                        <Typography
                          variant="caption"
                          color={theme.palette.text.secondary}
                          fontStyle="italic"
                        >
                          Estimated processing time: 2-14 days
                        </Typography>
                      </Tooltip>
                    </Box>
                  )}
                </Stack>
                {/* Right buttons */}
                <Stack direction="row" gap={1}>
                  <GeneratePdfButton reportId={report.tierIIReport.id} />
                  {isFilingComplete && <DownloadAllButton reportId={report.tierIIReport.id} />}
                </Stack>
              </Stack>
              <Stack maxWidth={500}>
                <ActivityStepper />
              </Stack>
            </Stack>
            <Stack>
              <Box maxWidth={448}>
                <Typography
                  variant="caption"
                  color={theme.palette.text.secondary}
                  fontStyle="italic"
                  lineHeight={1.25}
                >
                  There may be activities that happen after your report is filing complete, such as
                  receiving invoices from states or agencies. These activities are not reflected
                  here. Please reach out to Encamp Support if you have any questions.
                </Typography>
              </Box>
            </Stack>
          </Stack>
        )}
      </Stack>
    );
  }

  return <Outlet />;
};
